import React, { useState } from 'react';
import { Clock, RefreshCw, Phone, MessageSquare, ArrowRight, Calendar, UserCheck, ChevronRight } from 'lucide-react';
import { BrandingProvider } from '../../contexts/BrandingContext';

interface UniversalRequestUnderReviewProps {
  customerName?: string;
  companyName?: string;
  sessionId?: string;
  originalCategory?: string;
}

export const UniversalRequestUnderReview: React.FC<UniversalRequestUnderReviewProps> = ({
  customerName,
  companyName,
  sessionId,
  originalCategory
}) => {
  const [isSubmittingNew, setIsSubmittingNew] = useState(false);
  
  // Parse URL parameters for fallback values
  const urlParams = new URLSearchParams(window.location.search);
  const finalCustomerName = customerName || urlParams.get('customerName') || 'Customer';
  const finalCompanyName = companyName || urlParams.get('companyName') || 'Support Team';
  const finalSessionId = sessionId || urlParams.get('sessionId') || '';
  const finalCategory = originalCategory || urlParams.get('category') || 'consultation';

  const handleSubmitNewRequest = () => {
    setIsSubmittingNew(true);
    
    // Get the current org ID from the URL
    const currentPath = window.location.pathname;
    const orgMatch = currentPath.match(/^\/([a-zA-Z0-9_]+)/);
    const orgPrefix = orgMatch && orgMatch[1].startsWith('org_') ? `/${orgMatch[1]}` : '';
    
    // Redirect to the public request form with pre-filled data
    const formUrl = `${orgPrefix}/templates/universal/request-form?prefill_name=${encodeURIComponent(finalCustomerName)}&prefill_category=${encodeURIComponent(finalCategory)}&companyName=${encodeURIComponent(finalCompanyName)}&retry=true`;
    
    console.log('[RequestUnderReview] Redirecting to request form:', formUrl);
    window.location.href = formUrl;
  };

  const handleScheduleCallback = () => {
    // Create a mailto link with pre-filled information
    const subject = `Callback Request - ${finalCompanyName} Support`;
    const body = `Hello ${finalCompanyName} Team,

I would like to schedule a callback regarding my consultation request.

Details:
- Name: ${finalCustomerName}
- Original Category: ${finalCategory}
- Reference ID: ${finalSessionId}
- Preferred callback time: [Please specify]

Thank you!

Best regards,
${finalCustomerName}`;

    const mailtoUrl = `mailto:support@${finalCompanyName.toLowerCase().replace(/\s+/g, '')}.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
  };

  return (
    <BrandingProvider>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="container mx-auto px-4 py-8 max-w-md">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-4 mx-auto">
              <Clock className="w-12 h-12 text-blue-600 animate-pulse" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Request Under Review
            </h1>
            <p className="text-gray-600">
              Hi {finalCustomerName}, we're finding the perfect specialist for you
            </p>
          </div>

          {/* Status Card */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <UserCheck className="w-5 h-5 text-blue-600 mr-2" />
                <span className="font-semibold text-gray-900">Current Status</span>
              </div>
              <span className="bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full">
                In Review
              </span>
            </div>
            
            <div className="space-y-3 text-sm text-gray-600">
              <div className="flex justify-between">
                <span>Request ID:</span>
                <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                  {finalSessionId.substring(0, 20)}...
                </span>
              </div>
              <div className="flex justify-between">
                <span>Category:</span>
                <span className="capitalize font-medium text-gray-900">{finalCategory}</span>
              </div>
              <div className="flex justify-between">
                <span>Company:</span>
                <span className="font-medium text-gray-900">{finalCompanyName}</span>
              </div>
            </div>

            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800 leading-relaxed">
                <RefreshCw className="w-4 h-4 inline mr-1" />
                We're currently matching you with the most qualified specialist for your specific needs. This ensures you get the best possible assistance.
              </p>
            </div>
          </div>

          {/* Action Options */}
          <div className="space-y-3 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              What would you like to do?
            </h3>

            {/* Submit New Request */}
            <button
              onClick={handleSubmitNewRequest}
              disabled={isSubmittingNew}
              className="w-full bg-blue-600 text-white py-4 px-6 rounded-xl font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center disabled:opacity-75"
            >
              {isSubmittingNew ? (
                <>
                  <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                  Creating New Request...
                </>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5 mr-2" />
                  Submit New Request
                  <ChevronRight className="w-5 h-5 ml-2" />
                </>
              )}
            </button>

            {/* Schedule Callback */}
            <button
              onClick={handleScheduleCallback}
              className="w-full bg-green-600 text-white py-4 px-6 rounded-xl font-semibold hover:bg-green-700 transition-colors flex items-center justify-center"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Schedule Callback
              <ChevronRight className="w-5 h-5 ml-2" />
            </button>

            {/* Contact Support */}
            <button
              onClick={() => window.location.href = `mailto:support@${finalCompanyName.toLowerCase().replace(/\s+/g, '')}.com`}
              className="w-full bg-gray-100 text-gray-700 py-4 px-6 rounded-xl font-semibold hover:bg-gray-200 transition-colors flex items-center justify-center"
            >
              <MessageSquare className="w-5 h-5 mr-2" />
              Email Support Directly
              <ArrowRight className="w-5 h-5 ml-2" />
            </button>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Why is this happening?
            </h3>
            
            <div className="space-y-3">
              <div className="flex items-start p-3 bg-green-50 rounded-lg">
                <UserCheck className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-green-900">Quality Assurance</p>
                  <p className="text-xs text-green-700">
                    We're ensuring you get connected with the most qualified specialist for your specific needs.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start p-3 bg-blue-50 rounded-lg">
                <Clock className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Specialist Matching</p>
                  <p className="text-xs text-blue-700">
                    Our system is finding an expert who specializes in your type of consultation.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Expected Timeline */}
          <div className="bg-gradient-to-r from-indigo-500 to-blue-600 rounded-xl shadow-lg p-6 text-white text-center">
            <h3 className="text-lg font-semibold mb-2">Expected Response Time</h3>
            <div className="flex items-center justify-center space-x-2 mb-2">
              <Clock className="w-5 h-5" />
              <span className="text-2xl font-bold">15-30</span>
              <span className="text-lg">minutes</span>
            </div>
            <p className="text-sm text-blue-100">
              Most specialist matches are completed within this timeframe
            </p>
          </div>

          {/* Footer */}
          <div className="text-center mt-8 text-sm text-gray-500">
            <p>Need immediate assistance? Call our support line</p>
            <p className="mt-1 font-medium text-gray-700">
              <Phone className="w-4 h-4 inline mr-1" />
              Available 24/7 for urgent matters
            </p>
            <p className="mt-2 text-xs">
              Request submitted at {new Date().toLocaleTimeString()} • {finalCompanyName}
            </p>
          </div>
        </div>
      </div>
    </BrandingProvider>
  );
};